import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { 
  CheckCircleIcon, 
  PencilIcon, 
  ArrowsRightLeftIcon, 
  SparklesIcon,
  DocumentTextIcon,
  ClipboardDocumentIcon,
  ArrowPathIcon,
  ChatBubbleLeftRightIcon
} from '@heroicons/react/24/outline'
import clsx from 'clsx'
import { CitationSuggester } from '../citations/CitationSuggester'
import InputDialog from '../common/InputDialog'

interface SelectionToolbarProps {
  selectedText: string
  selectionStart?: number
  selectionEnd?: number
  onAITask: (taskType: string, text: string, instructions?: string) => void
  isProcessing: boolean
  documentContext?: string
  researchField?: string
  onCitationInsert?: (citation: string, paper?: any) => void
  onComment?: (data: { anchor: { start: number; end: number; quote: string }, body: string }) => void
  documentMode?: 'markdown' | 'latex' | 'plain'
  documentId?: string | null
}

const SelectionToolbar: React.FC<SelectionToolbarProps> = ({
  selectedText,
  selectionStart,
  selectionEnd,
  onAITask,
  isProcessing,
  documentContext,
  researchField,
  onCitationInsert,
  onComment,
  documentMode = 'markdown',
  documentId,
}) => {
  const [rewriteDialogOpen, setRewriteDialogOpen] = useState(false)
  const [commentDialogOpen, setCommentDialogOpen] = useState(false)
  const [processingTaskId, setProcessingTaskId] = useState<string | null>(null)

  // Reset processing task ID when processing completes
  useEffect(() => {
    if (!isProcessing) {
      setProcessingTaskId(null)
    }
  }, [isProcessing])

  const handleAITask = async (taskId: string, taskType: string, text: string, instructions?: string) => {
    setProcessingTaskId(taskId)
    try {
      await onAITask(taskType, text, instructions)
    } catch (error) {
      // Error handling is done in parent
      setProcessingTaskId(null)
    }
  }

  const toolbarItems = [
    {
      id: 'proofread',
      label: 'Proofread',
      icon: CheckCircleIcon,
      description: 'Fix grammar and spelling',
      color: 'text-green-600 hover:bg-green-50',
      action: () => handleAITask('proofread', 'proofread', selectedText),
    },
    {
      id: 'rewrite',
      label: 'Rewrite',
      icon: PencilIcon,
      description: 'Improve clarity and style',
      color: 'text-blue-600 hover:bg-blue-50',
      action: () => setRewriteDialogOpen(true),
    },
    {
      id: 'expand',
      label: 'Expand',
      icon: ArrowsRightLeftIcon,
      description: 'Add more detail',
      color: 'text-purple-600 hover:bg-purple-50',
      action: () => handleAITask('expand', 'expand', selectedText),
    },
    {
      id: 'summarize',
      label: 'Summarize',
      icon: DocumentTextIcon,
      description: 'Make it more concise',
      color: 'text-orange-600 hover:bg-orange-50',
      action: () => handleAITask('summarize', 'summarize', selectedText),
    },
    {
      id: 'explain',
      label: 'Explain',
      icon: SparklesIcon,
      description: 'Add explanation or context',
      color: 'text-indigo-600 hover:bg-indigo-50',
      action: () => handleAITask('explain', 'explain', selectedText),
    },
  ]

  const handleRewriteConfirm = (instructions: string) => {
    if (instructions.trim()) {
      handleAITask('rewrite', 'rewrite', selectedText, instructions)
    }
  }

  const handleCopy = () => {
    navigator.clipboard.writeText(selectedText)
  }

  const handleComment = () => {
    if (onComment && selectionStart !== undefined && selectionEnd !== undefined) {
      setCommentDialogOpen(true)
    }
  }

  const handleCommentConfirm = (body: string) => {
    if (onComment && selectionStart !== undefined && selectionEnd !== undefined && body.trim()) {
      onComment({
        anchor: {
          start: selectionStart,
          end: selectionEnd,
          quote: selectedText
        },
        body: body.trim()
      })
      setCommentDialogOpen(false)
    }
  }

  return (
    <div className="flex items-center gap-1 p-1 overflow-x-auto scrollbar-hide" style={{ maxWidth: '100%' }}>
      {/* AI Tools */}
      {toolbarItems.map((item, index) => {
        const isTaskProcessing = isProcessing && processingTaskId === item.id
        return (
          <motion.button
            key={item.id}
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: index * 0.05 }}
            onClick={item.action}
            disabled={isProcessing}
            className={clsx(
              'group relative flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-all duration-150',
              'disabled:opacity-50 disabled:cursor-not-allowed',
              'hover:shadow-sm',
              item.color
            )}
            title={isTaskProcessing ? 'Processing...' : item.description}
          >
            {isTaskProcessing ? (
              <ArrowPathIcon className="w-4 h-4 animate-spin" />
            ) : (
              <item.icon className="w-4 h-4" />
            )}
            <span className="hidden sm:inline">
              {isTaskProcessing ? 'Processing...' : item.label}
            </span>
            
            {/* Tooltip */}
            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 text-xs text-white bg-gray-900 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50">
              {isTaskProcessing ? 'Processing...' : item.description}
            </div>
          </motion.button>
        )
      })}
      
      {/* Divider */}
      <div className="w-px h-6 bg-gray-300 mx-1" />
      
      {/* Citation Suggester */}
      <CitationSuggester
        selectedText={selectedText}
        documentContext={documentContext}
        researchField={researchField}
        onCitationInsert={onCitationInsert}
        documentMode={documentMode}
        documentId={documentId}
      />
      
      {/* Divider */}
      <div className="w-px h-6 bg-gray-300 mx-1" />
      
      {/* Comment Button */}
      {onComment && selectionStart !== undefined && selectionEnd !== undefined && (
        <>
          <motion.button
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: toolbarItems.length * 0.05 }}
            onClick={handleComment}
            className="group relative flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium text-cyan-600 hover:bg-cyan-50 transition-all duration-150"
            title="Add comment on selected text"
          >
            <ChatBubbleLeftRightIcon className="w-4 h-4" />
            <span className="hidden sm:inline">Comment</span>
            
            {/* Tooltip */}
            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 text-xs text-white bg-gray-900 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50">
              Add comment on selected text
            </div>
          </motion.button>
          
          {/* Divider */}
          <div className="w-px h-6 bg-gray-300 mx-1" />
        </>
      )}
      
      {/* Copy Button */}
      <motion.button
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: toolbarItems.length * 0.05 }}
        onClick={handleCopy}
        className="group relative flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium text-gray-600 hover:bg-gray-50 transition-all duration-150"
        title="Copy selected text"
      >
        <ClipboardDocumentIcon className="w-4 h-4" />
        <span className="hidden sm:inline">Copy</span>
        
        {/* Tooltip */}
        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 text-xs text-white bg-gray-900 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50">
          Copy selected text
        </div>
      </motion.button>
      
      {/* Selected text info */}
      <div className="hidden md:flex items-center gap-2 px-3 py-2 text-xs text-gray-500 border-l border-gray-200 ml-1">
        <span>{selectedText.split(/\s+/).length} words</span>
        <span>•</span>
        <span>{selectedText.length} chars</span>
      </div>

      {/* Rewrite Instruction Dialog */}
      <InputDialog
        isOpen={rewriteDialogOpen}
        onClose={() => setRewriteDialogOpen(false)}
        onConfirm={handleRewriteConfirm}
        title="Rewrite Text"
        message="How would you like to rewrite this text?"
        placeholder="e.g., Make it more formal, Simplify the language, Make it more concise"
        defaultValue="Make it more formal"
        confirmText="Rewrite"
        cancelText="Cancel"
        inputType="text"
        maxLength={200}
      />

      {/* Comment Dialog */}
      <InputDialog
        isOpen={commentDialogOpen}
        onClose={() => setCommentDialogOpen(false)}
        onConfirm={handleCommentConfirm}
        title="Add Comment"
        message={`Comment on: "${selectedText.slice(0, 50)}${selectedText.length > 50 ? '...' : ''}"`}
        placeholder="Enter your comment..."
        confirmText="Add Comment"
        cancelText="Cancel"
        inputType="textarea"
      />
    </div>
  )
}

export default SelectionToolbar